import React from "react";
import { useParams } from "@tanstack/react-router";
import {
  Lightbulb,
  Shield,
  Brain,
  Database,
  Zap,
  Clock,
  AlertTriangle,
  CheckCircle,
  Settings,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { AgentLayout } from "./AgentLayout";
import { extractRecommendationsFromData } from "./utils";

// Get actual recommendations from data
const actualRecommendations = extractRecommendationsFromData();

interface FailedTest {
  id: string;
  testName: string;
  input: string;
  expectedOutput: string;
  actualOutput: string;
  responseTime: number;
  severity: "high" | "medium" | "low";
}

interface ImprovementAction {
  id: string;
  title: string;
  description: string;
  priority: "critical" | "high" | "medium" | "low";
  topic: string;
  actions: string[];
  icon: any;
  failedTests: FailedTest[];
}

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case "critical":
      return "bg-red-900/50 text-red-300 border-red-500/50";
    case "high":
      return "bg-red-900/30 text-red-400 border-red-600/30";
    case "medium":
      return "bg-yellow-900/30 text-yellow-400 border-yellow-600/30";
    case "low":
      return "bg-blue-900/30 text-blue-400 border-blue-600/30";
    default:
      return "bg-gray-900/30 text-gray-400 border-gray-600/30";
  }
};

const getSeverityColor = (severity: string) => {
  switch (severity) {
    case "high":
      return "bg-red-900/30 text-red-400 border-red-600/30";
    case "medium":
      return "bg-yellow-900/30 text-yellow-400 border-yellow-600/30";
    case "low":
      return "bg-blue-900/30 text-blue-400 border-blue-600/30";
    default:
      return "bg-gray-900/30 text-gray-400 border-gray-600/30";
  }
};

export const RecommendationDetailsPage: React.FC = () => {
  const { agentId, recommendationId } = useParams({
    from: "/ai-agents/$agentId/recommendations/$recommendationId",
  });

  const recommendation = actualRecommendations.find(
    (action: any) => action.id === recommendationId,
  );

  if (!recommendation) {
    return (
      <AgentLayout activeTab="overview">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Recommendation Not Found</h1>
          <p className="text-gray-400 mb-6">
            The requested recommendation could not be found.
          </p>
        </div>
      </AgentLayout>
    );
  }

  const Icon = recommendation.icon;

  return (
    <AgentLayout activeTab="overview">
      <div className="space-y-6">
        {/* Recommendation Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Icon className="h-8 w-8 text-blue-400" />
            <div>
              <h1 className="text-xl lg:text-2xl font-bold text-gray-100">
                {recommendation.title}
              </h1>
              <p className="text-sm text-gray-400">
                {recommendation.description}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Badge className="bg-gray-600 text-gray-300 border-gray-500">
              {recommendation.topic}
            </Badge>
            <Badge
              className={`${getPriorityColor(recommendation.priority)} border`}
            >
              {recommendation.priority.toUpperCase()}
            </Badge>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Overview */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-gray-100 flex items-center space-x-2">
                  <Lightbulb className="h-5 w-5 text-yellow-400" />
                  <span>Recommendation Overview</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-300">Topic:</span>
                    <span className="text-gray-400 ml-2">
                      {recommendation.topic}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-300">Priority:</span>
                    <Badge
                      className={`${getPriorityColor(recommendation.priority)} border ml-2`}
                    >
                      {recommendation.priority.toUpperCase()}
                    </Badge>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-gray-100 mb-3">
                    Why This Action Was Recommended:
                  </h4>
                  <p className="text-gray-400 mb-4">
                    {recommendation.description}
                  </p>

                  <h5 className="font-medium text-gray-100 mb-3">
                    Actions to Take:
                  </h5>
                  <ul className="space-y-2">
                    {recommendation.actions.map(
                      (action: string, index: number) => (
                        <li
                          key={index}
                          className="text-sm text-gray-400 flex items-start space-x-2"
                        >
                          <span className="text-blue-400 mt-1">•</span>
                          <span>{action}</span>
                        </li>
                      ),
                    )}
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Failed Tests */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-gray-100 flex items-center space-x-2">
                  <AlertTriangle className="h-5 w-5 text-red-400" />
                  <span>Failed Test Examples</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {recommendation.failedTests.map((test: any) => (
                  <div
                    key={test.id}
                    className="border border-gray-700 rounded-lg p-4 bg-gray-750"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h6 className="font-medium text-gray-100">
                        {test.testName}
                      </h6>
                      <Badge
                        className={`${getSeverityColor(test.severity)} border`}
                      >
                        {test.severity.toUpperCase()}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-1 gap-3 text-sm">
                      <div>
                        <span className="font-medium text-gray-300">
                          Input:
                        </span>
                        <div className="mt-1 p-2 bg-gray-700 rounded text-gray-300 font-mono text-xs">
                          {test.input}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <span className="font-medium text-gray-300">
                            Expected Output:
                          </span>
                          <div className="mt-1 p-2 bg-green-900/30 border border-green-600/30 rounded text-green-300 font-mono text-xs">
                            {test.expectedOutput}
                          </div>
                        </div>
                        <div>
                          <span className="font-medium text-gray-300">
                            Actual Output:
                          </span>
                          <div className="mt-1 p-2 bg-red-900/30 border border-red-600/30 rounded text-red-300 font-mono text-xs">
                            {test.actualOutput}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-4 text-xs">
                        <span className="text-gray-400">
                          Response Time:{" "}
                          <span className="text-yellow-400">
                            {test.responseTime}ms
                          </span>
                        </span>
                        <span className="text-gray-400">
                          Test ID:{" "}
                          <span className="text-blue-400">{test.id}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-gray-100">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button className="w-full justify-start" variant="outline">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Mark as Implemented
                </Button>
                <Button className="w-full justify-start" variant="outline">
                  <Clock className="h-4 w-4 mr-2" />
                  Schedule Review
                </Button>
                <Button className="w-full justify-start" variant="outline">
                  <Settings className="h-4 w-4 mr-2" />
                  Create Task
                </Button>
              </CardContent>
            </Card>

            {/* Metrics */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-gray-100">Impact Metrics</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-300">Failed Tests</span>
                  <Badge className="bg-red-900/30 text-red-400 border-red-600/30">
                    {recommendation.failedTests.length}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-300">
                    Avg Response Time
                  </span>
                  <span className="text-sm text-gray-400">
                    {Math.round(
                      recommendation.failedTests.reduce(
                        (sum: number, test: any) => sum + test.responseTime,
                        0,
                      ) / recommendation.failedTests.length,
                    )}
                    ms
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-300">High Severity</span>
                  <Badge className="bg-red-900/30 text-red-400 border-red-600/30">
                    {
                      recommendation.failedTests.filter(
                        (t: any) => t.severity === "high",
                      ).length
                    }
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </AgentLayout>
  );
};
